import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { WhatsappLogo, Prohibit, Trash, CheckCircle, XCircle } from "@phosphor-icons/react";
import { API, authHeaders } from "@/lib/api";
import { useLocale } from "@/i18n";
import { formatPrice } from "../booking/format";

const toISO = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function buildDays(n) {
  const days = [];
  const today = new Date();
  for (let i = 0; i < n; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    days.push(d);
  }
  return days;
}

const STATUS_STYLES = {
  confirmado: "bg-amber-500/15 text-amber-500",
  concluido: "bg-emerald-500/15 text-emerald-400",
  cancelado: "bg-red-500/15 text-red-400",
};

export default function AgendaTab({ country }) {
  const { t, intlLocale, locale } = useLocale();
  const waPrefix = country === "py" ? "595" : "55";
  const STATUS_LABELS = { confirmado: t("status_confirmed"), concluido: t("status_completed"), cancelado: t("status_canceled") };
  const weekdayFmt = useMemo(() => new Intl.DateTimeFormat(intlLocale, { weekday: "short" }), [intlLocale]);
  const dayFmt = useMemo(() => new Intl.DateTimeFormat(intlLocale, { day: "2-digit", month: "short" }), [intlLocale]);
  const days = useMemo(() => buildDays(14), []);
  const [date, setDate] = useState(() => toISO(new Date()));
  const [bookings, setBookings] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [blockTime, setBlockTime] = useState("");
  const [blockDuration, setBlockDuration] = useState("30");
  const [blockReason, setBlockReason] = useState("");
  const reqSeq = useRef(0);

  const load = useCallback(async (d) => {
    const seq = ++reqSeq.current;
    setLoading(true);
    try {
      const [b, bl] = await Promise.all([
        axios.get(`${API}/barber/bookings`, { params: { date: d }, headers: authHeaders() }),
        axios.get(`${API}/barber/blocks`, { params: { date: d }, headers: authHeaders() }),
      ]);
      if (seq !== reqSeq.current) return;
      setBookings(b.data);
      setBlocks(bl.data);
    } catch {
      if (seq === reqSeq.current) toast.error(t("agenda_error"));
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load(date);
  }, [date, load]);

  const setStatus = async (id, status) => {
    try {
      await axios.post(`${API}/barber/bookings/${id}/status`, { status }, { headers: authHeaders() });
      toast.success(status === "concluido" ? t("completed_ok") : t("canceled_ok"));
      load(date);
    } catch {
      toast.error(t("update_error"));
    }
  };

  const addBlock = async (e) => {
    e.preventDefault();
    if (!blockTime) return toast.warning(t("warn_block_time"));
    try {
      await axios.post(
        `${API}/barber/blocks`,
        { date, time: blockTime, duration_min: Number(blockDuration), reason: blockReason },
        { headers: authHeaders() }
      );
      toast.success(t("block_ok"));
      setBlockTime("");
      setBlockReason("");
      load(date);
    } catch {
      toast.error(t("block_error"));
    }
  };

  const removeBlock = async (id) => {
    try {
      await axios.delete(`${API}/barber/blocks/${id}`, { headers: authHeaders() });
      toast.success(t("block_removed"));
      load(date);
    } catch {
      toast.error(t("block_remove_error"));
    }
  };

  return (
    <div data-testid="agenda-tab">
      <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-4 px-4 pb-1" data-testid="admin-date-strip">
        {days.map((d) => {
          const iso = toISO(d);
          const active = date === iso;
          return (
            <button
              key={iso}
              data-testid={`admin-date-chip-${iso}`}
              onClick={() => setDate(iso)}
              className={`shrink-0 flex flex-col items-center justify-center min-w-[60px] h-[64px] rounded-xl border transition-colors duration-200 ${
                active
                  ? "bg-amber-500 text-zinc-950 border-amber-500"
                  : "bg-zinc-900 border-zinc-800 text-zinc-300 hover:border-zinc-700"
              }`}
            >
              <span className={`text-[11px] uppercase font-semibold ${active ? "text-zinc-800" : "text-zinc-500"}`}>
                {weekdayFmt.format(d).replace(".", "")}
              </span>
              <span className="text-sm font-bold mt-0.5">{dayFmt.format(d).replace(".", "")}</span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex justify-center py-10" data-testid="admin-loading">
          <div className="h-8 w-8 rounded-full border-2 border-amber-500 border-t-transparent animate-spin" />
        </div>
      ) : (
        <>
          <section className="mt-6" data-testid="admin-bookings-list">
            <h2 className="text-sm font-bold text-zinc-300 mb-3">
              {t("agenda_count")} ({bookings.length})
            </h2>
            {bookings.length === 0 ? (
              <p className="text-sm text-zinc-500 bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid="no-bookings-msg">
                {t("no_bookings")}
              </p>
            ) : (
              <div className="space-y-3">
                {bookings.map((b) => (
                  <div key={b.id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid={`booking-row-${b.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-bold text-zinc-100">
                        {b.time} · {b.client_name}
                      </p>
                      <span className={`text-xs font-bold px-2 py-1 rounded-full ${STATUS_STYLES[b.status] || ""}`} data-testid={`booking-status-${b.id}`}>
                        {STATUS_LABELS[b.status] || b.status}
                      </span>
                    </div>
                    <p className="text-sm text-zinc-400 mt-1">
                      {b.service_name} · {b.duration_min} min · {formatPrice(b.price, locale)}
                      {b.professional_name && <span className="text-amber-500/90 font-semibold"> · {b.professional_name}</span>}
                    </p>
                    <div className="flex items-center justify-between mt-3">
                      <a
                        href={`https://wa.me/${b.client_phone.length <= 11 ? waPrefix + b.client_phone : b.client_phone}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-amber-500 transition-colors"
                        data-testid={`booking-whatsapp-${b.id}`}
                      >
                        <WhatsappLogo size={14} weight="duotone" className="text-amber-500" />
                        {b.client_phone}
                      </a>
                      {b.status === "confirmado" && (
                        <div className="flex gap-2">
                          <button
                            data-testid={`booking-complete-btn-${b.id}`}
                            onClick={() => setStatus(b.id, "concluido")}
                            className="flex items-center gap-1 text-xs font-semibold text-emerald-400 border border-emerald-500/40 rounded-lg px-2.5 py-1.5 hover:bg-emerald-500/10 transition-colors"
                          >
                            <CheckCircle size={13} weight="bold" /> {t("complete")}
                          </button>
                          <button
                            data-testid={`booking-cancel-btn-${b.id}`}
                            onClick={() => setStatus(b.id, "cancelado")}
                            className="flex items-center gap-1 text-xs font-semibold text-red-400 border border-red-500/40 rounded-lg px-2.5 py-1.5 hover:bg-red-500/10 transition-colors"
                          >
                            <XCircle size={13} weight="bold" /> {t("cancel")}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="mt-8" data-testid="admin-blocks-section">
            <h2 className="text-sm font-bold text-zinc-300 mb-3 flex items-center gap-2">
              <Prohibit size={16} className="text-amber-500" weight="duotone" /> {t("block_title")}
            </h2>
            <form onSubmit={addBlock} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-3" data-testid="block-form">
              <div className="flex gap-3">
                <input
                  data-testid="block-time-input"
                  type="time"
                  value={blockTime}
                  onChange={(e) => setBlockTime(e.target.value)}
                  className="flex-1 h-11 px-3 rounded-lg bg-zinc-950 border border-zinc-800 text-zinc-100 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 focus:outline-none"
                />
                <select
                  data-testid="block-duration-select"
                  value={blockDuration}
                  onChange={(e) => setBlockDuration(e.target.value)}
                  className="h-11 px-3 rounded-lg bg-zinc-950 border border-zinc-800 text-zinc-100 focus:border-amber-500 focus:outline-none"
                >
                  <option value="30">30 min</option>
                  <option value="60">1h</option>
                  <option value="90">1h30</option>
                  <option value="120">2h</option>
                </select>
              </div>
              <input
                data-testid="block-reason-input"
                type="text"
                value={blockReason}
                onChange={(e) => setBlockReason(e.target.value)}
                placeholder={t("block_reason_ph")}
                className="w-full h-11 px-3 rounded-lg bg-zinc-950 border border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 focus:outline-none"
              />
              <button
                data-testid="block-submit-btn"
                type="submit"
                className="w-full h-11 rounded-lg bg-amber-500 text-zinc-950 font-bold hover:bg-amber-400 transition-colors active:scale-95"
              >
                {t("block_btn")}
              </button>
            </form>

            {blocks.length > 0 && (
              <div className="mt-3 space-y-2" data-testid="admin-blocks-list">
                {blocks.map((bl) => (
                  <div key={bl.id} className="flex items-center justify-between bg-zinc-900 border border-zinc-800 rounded-lg px-4 py-3" data-testid={`block-row-${bl.id}`}>
                    <p className="text-sm text-zinc-300">
                      <span className="font-bold">{bl.time}</span> · {bl.duration_min} min
                      {bl.reason && <span className="text-zinc-500"> · {bl.reason}</span>}
                    </p>
                    <button
                      data-testid={`block-remove-btn-${bl.id}`}
                      onClick={() => removeBlock(bl.id)}
                      className="text-zinc-500 hover:text-red-400 transition-colors"
                    >
                      <Trash size={16} weight="bold" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
